/**
 * Places hooks — suggestions from the platform's Google Places proxy.
 */
import { useQuery } from '@tanstack/react-query';

import { useSdkContext } from '../providers/SdkProvider';
import { useAuth } from '../providers/AuthProvider';
import { createPlacesService } from '../services/places.service';

export const placesKeys = {
  all: ['places'] as const,
  cities: (country: string, query: string) =>
    [...placesKeys.all, 'cities', country, query] as const,
};

/** The server refuses a query shorter than this. */
export const PLACE_QUERY_MIN_LENGTH = 2;

/**
 * City suggestions inside one country, for "the city you live in"
 * (Citizen first run · D-2). The caller debounces `query`; this hook only
 * decides whether it is worth asking. A failed search is not retried: the
 * field stays a free-text input, so the citizen can always type their city.
 */
export function useCitySuggestions(query: string, countryCode: string | null | undefined) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const service = createPlacesService(client);
  const trimmed = query.trim();

  return useQuery({
    queryKey: placesKeys.cities(countryCode ?? '', trimmed),
    queryFn: () =>
      service.autocomplete(trimmed, {
        countries: countryCode ? [countryCode] : undefined,
        kind: 'city',
      }),
    enabled: isSignedIn && !!token && !!countryCode && trimmed.length >= PLACE_QUERY_MIN_LENGTH,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}
